import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useField } from 'react-final-form';
import { useFieldArray } from 'react-final-form-arrays';
import { observer } from 'mobx-react';
import { Icon } from 'ksnm-common-ui/lib/Icon';
import { IconButton } from 'ksnm-common-ui/lib/IconButton';
import { Table as KSNMTable } from 'ksnm-common-ui/lib/Table2/Table';
import { getLocalRowsByCheckBoxStatus } from 'ksnm-common-ui/lib/Table2/utils/getLocalRowsByCheckBoxStatus';
import { maxBy, sortBy } from 'lodash-es';
import type { LocalDataSource } from 'ksnm-common-ui/lib/Table2/contexts/Table/TableContext.interface';
import type { ResponseDataRow } from 'ksnm-common-ui/lib/Table2/utils/TableRequest';

import { useTableTitleWithCount } from '@src/utils/hooks/useTableTitleWithCount';
import { useUpdateTable } from '@src/utils/hooks/useUpdateTable';
import { confirmDialogStore } from '@stores/confirmDialog';
import { constantStore } from '@stores/constantStore';
import { designDrawerStore } from '@stores/designDrawerStore';
import { controlVersionEditStore } from '@stores/dictionaryStores/controlVersionsStore/controlVersionEditStore';
import type { DrawerButtonInterface } from '@stores/designDrawerStore';

import { columns } from './columns';
import { Form } from './Form';
import { validate } from './validateSchema';

const { TABLE_NAMES } = constantStore;

export const StepTable = observer(() => {
  const { fields } = useFieldArray('steps');

  const { isEditMode } = controlVersionEditStore;
  const { setCustomForm } = designDrawerStore;
  const { open: openConfirmDialog } = confirmDialogStore;

  const [checked, setChecked] = useState<ResponseDataRow[]>([]);

  const [key, update, externalControl] = useUpdateTable({ tableName: TABLE_NAMES.DICTIONARY_CONTROL_STEPS });

  useEffect(() => {
    update();
  }, [fields.value, update]);

  const {
    input: { value: ctrlVersCode },
  } = useField('ctrlVersCode');

  const localData = useMemo<LocalDataSource>(
    () => ({
      data: sortBy(fields.value ?? [], 'stepNum'),
      fieldFormat: {
        ctrlVersCode: 'string',
        stepNum: 'number',
        stepCodeExt: 'string',
        stepType: 'string',
        stepName: 'string',
        stepDesc: 'string',
        textSuccess: 'string',
        textFail: 'string',
      },
    }),
    [fields.value],
  );

  const onOpenDrawer = useCallback(
    (formData) => {
      const buttons: DrawerButtonInterface[] = [
        {
          key: 'save',
          editButton: !isEditMode,
          label: () => <Icon name="save" rightElement="Применить" />,
          submitButton: true,
        },
      ];

      if (formData.id) {
        buttons.unshift({
          editButton: !isEditMode,
          key: 'delete',
          label: () => <Icon name="delete" rightElement="Удалить" fill="#3D5A80" />,
          onClick: () => {
            confirmDialogStore.open({
              onOk: () => {
                const index = fields.value.findIndex((f) => f.id === formData.id);
                setCustomForm(null);
                fields.remove(index);
              },
              text: 'Удалить шаг?',
            });
          },
        });
      }

      setCustomForm({
        form: () => <Form />,
        formData: formData ?? {},
        editMode: !isEditMode,
        validate,
        submit: (values) => {
          const index = (fields.value || [])?.findIndex((f) => f.id === values.id);
          if (index >= 0) {
            fields.remove(index);
          }
          fields.insert(index >= 0 ? index : fields.value?.length, { ...values, id: values.stepNum });
          update();
        },
        buttons,
        title: 'Контрольный шаг',
      });
    },
    [setCustomForm, isEditMode, fields, update],
  );

  const onRowClick = useCallback(
    (value) => {
      onOpenDrawer({
        ...value,
        delta: typeof value.delta === 'number' ? `${value.delta}` : undefined,
        decimalQty: typeof value.decimalQty === 'number' ? `${value.decimalQty}` : undefined,
      });
    },
    [onOpenDrawer],
  );

  const onCheckBoxChange = useCallback(
    async (tr, sr) => {
      const rows = await getLocalRowsByCheckBoxStatus({
        tableRequest: tr,
        localData,
        selectRows: sr,
        keyField: 'stepNum',
      });
      setChecked(rows);
    },
    [localData, setChecked],
  );

  const onCreate = useCallback(() => {
    const maxStepNum = (maxBy(fields.value ?? [], 'stepNum')?.stepNum ?? 0) + 1;
    onOpenDrawer({ stepNum: maxStepNum, ctrlType: 'L' });
  }, [fields.value, onOpenDrawer]);

  const onDelete = useCallback(() => {
    openConfirmDialog({
      text: `Удалить выбранные элементы (${checked.length})?`,
      onOk: () => {
        const deleteIndex = checked.map(({ id }) => fields.value.findIndex((f) => f.id === id));

        deleteIndex
          .sort()
          .reverse()
          .forEach((index) => fields.remove(index));

        fields.value
          .filter((_, idx) => !deleteIndex.includes(idx))
          .forEach((item, idx) => fields.update(idx, { ...item, stepNum: idx + 1, id: idx + 1 }));
      },
    });
  }, [checked, fields, openConfirmDialog]);

  const onSwap = useCallback(() => {
    const aIndex = fields.value?.findIndex((item) => item.id === checked[0].id);
    const bIndex = fields.value?.findIndex((item) => item.id === checked[1].id);

    fields.update(aIndex, { ...checked[0], stepNum: checked[1].stepNum, id: checked[1].id });
    fields.update(bIndex, { ...checked[1], stepNum: checked[0].stepNum, id: checked[0].id });

    update();
  }, [checked, fields, update]);

  const [titleWithCount, onTableChange] = useTableTitleWithCount('Контрольные шаги');

  const extendTableButtons = (
    <>
      <IconButton onClick={onCreate} iconName="add" title="Добавить" disabled={!isEditMode} />
      <IconButton
        onClick={onSwap}
        iconName="refresh"
        title="Поменять номера шагов"
        disabled={checked.length !== 2 || !isEditMode}
      />
      <IconButton onClick={onDelete} disabled={checked.length === 0 || !isEditMode} iconName="delete" title="Удалить" />
    </>
  );

  if (!ctrlVersCode) return null;

  return (
    <KSNMTable
      externalControl={externalControl}
      tableName={TABLE_NAMES.DICTIONARY_CONTROL_STEPS}
      columns={columns}
      tableTitle={titleWithCount}
      resource=""
      keyField="stepNum"
      localDataSource={localData}
      onRowClick={onRowClick}
      onCheckBoxChange={onCheckBoxChange}
      extendTableButtons={extendTableButtons}
      showAggregateRow={false}
      key={key}
      onTableChange={onTableChange}
    />
  );
});
